import React, { useEffect, useState } from 'react';
import { Image, Proportion } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import Slider from 'react-slick';
import MagicSliderDots from 'react-magic-slider-dots';
import 'slick-carousel/slick/slick.scss';
import 'slick-carousel/slick/slick-theme.scss';
import s from './SlickCarousel.module.scss';

interface ArrowProps {
  children?: JSX.Element;
  className?: string;
  onClick?: () => void;
  setInteractionHappened: (x: boolean) => void;
  hover?: boolean;
  focus?: boolean;
}
function RightArrow() {
  return (
    <svg
      width={25}
      height={24}
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M.5 12c0-6.627 5.373-12 12-12s12 5.373 12 12-5.373 12-12 12-12-5.373-12-12z"
        fill="#333853"
        fillOpacity={0.54}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.345 8.143a.495.495 0 01.003.7L11.213 12l3.136 3.164a.497.497 0 01-.002.701.494.494 0 01-.699 0L9.799 12l3.843-3.855a.498.498 0 01.703-.002z"
        fill="#fff"
      />
    </svg>
  );
}

function LeftArrow() {
  return (
    <svg
      width={25}
      height={24}
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M.5 12c0-6.627 5.373-12 12-12s12 5.373 12 12-5.373 12-12 12-12-5.373-12-12z"
        fill="#333853"
        fillOpacity={0.54}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.354 8.148L15.2 12l-3.852 3.854a.495.495 0 11-.7-.7L13.786 12l-3.139-3.153a.495.495 0 01.002-.7.498.498 0 01.704.001z"
        fill="#fff"
      />
    </svg>
  );
}

function Arrow(props: ArrowProps) {
  const { children, className, onClick, setInteractionHappened, hover, focus } =
    props;
  return (
    <button
      data-hook="carousel-arrow"
      className={className}
      onClick={(e) => {
        e.stopPropagation();
        setInteractionHappened(true);
        onClick!();
      }}
      style={{ opacity: hover || focus ? 1 : 0 }}
    >
      {children}
    </button>
  );
}

interface ImageSources {
  mediaSource: string;
  backupSource?: string;
  siteName?: string | undefined;
}

function SlickCarousel({
  hover,
  focus,
  imageSources,
}: {
  hover: boolean;
  focus: boolean;
  imageSources: Array<ImageSources>;
}) {
  const { t } = useTranslation();
  const [slideRef, setSlideRef] = useState<any>();
  const [interactionHappened, setInteractionHappened] = useState(false);

  const settings = {
    className: s.providerCardSlickCarousel,
    cssEase: 'linear',
    dots: true,
    pauseOnHover: false,
    autoplay: false,
    autoplaySpeed: 2000,
    onSwipe: () => {
      setInteractionHappened(true);
    },
    lazyLoad: 'progressive',
    nextArrow: (
      <Arrow
        setInteractionHappened={setInteractionHappened}
        hover={hover}
        focus={focus}
      >
        <LeftArrow />
      </Arrow>
    ),
    prevArrow: (
      <Arrow
        setInteractionHappened={setInteractionHappened}
        hover={hover}
        focus={focus}
      >
        <RightArrow />
      </Arrow>
    ),
    appendDots: (dots: any) => {
      return (
        <div
          data-hook="dots-wrapper"
          onClick={(e) => {
            e.stopPropagation();
            setInteractionHappened(true);
          }}
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            bottom: '17px',
            zIndex: 2,
          }}
          tabIndex={-1}
          aria-hidden="true"
        >
          <MagicSliderDots
            dots={dots}
            numDotsToShow={5}
            dotWidth={10}
            dotContainerClassName="magic-dots slick-dots magic-dots-container"
          />
        </div>
      );
    },
  };

  useEffect(() => {
    setInteractionHappened(false);
  }, [hover]);

  useEffect(() => {
    if (slideRef) {
      if (hover && !interactionHappened) {
        slideRef.slickPlay();
      } else {
        slideRef.slickPause();
      }
    }
  }, [hover, slideRef, interactionHappened]);

  return (
    <div
      data-hook="carousel"
      role="region"
      aria-label={t('marketplace-explore-aria-label-carousel')}
      style={{ display: 'block', minHeight: '148px' }}
    >
      <div className={s.root}>
        <Slider ref={(slider: any) => setSlideRef(slider)} {...settings}>
          {Array.isArray(imageSources) &&
            imageSources.map((slide, id) => (
              <Proportion key={id} aspectRatio={39 / 20}>
                <Image
                  dataHook={`slide-${id}`}
                  fit="cover"
                  position="top"
                  transparent
                  borderRadius="0px 0px 0px 0px"
                  alt={slide.siteName || ''}
                  src={slide.mediaSource}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    if (slide.backupSource) {
                      e.currentTarget.src = slide.backupSource;
                    }
                  }}
                ></Image>
              </Proportion>
            ))}
        </Slider>
      </div>
    </div>
  );
}

export default SlickCarousel;
